import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

export default function Purchases() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "", bill_no: "", bill_date: new Date().toISOString().slice(0,10),
    due_date: "", currency: "INR", notes: "",
    lines: [{ name: "", hsn: "", quantity: 1, price: 0, tax_rate: 18 }],
  });

  const load = async () => setRows((await api.get("/purchases")).data);
  useEffect(() => {
    load();
    api.get("/vendors").then((r) => setVendors(r.data));
  }, []);

  const setLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  const addLine = () => setForm({ ...form, lines: [...form.lines, { name:"", hsn:"", quantity:1, price:0, tax_rate:18 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.length > 1 ? form.lines.filter((_,idx) => idx !== i) : form.lines });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/purchases", { ...form, lines: form.lines.map((l) => ({
        ...l, quantity: Number(l.quantity), price: Number(l.price), tax_rate: Number(l.tax_rate),
      })) });
      toast.success("Purchase bill saved");
      setShow(false);
      setForm({ vendor_id:"", bill_no:"", bill_date: new Date().toISOString().slice(0,10), due_date:"", currency:"INR", notes:"", lines:[{ name:"", hsn:"", quantity:1, price:0, tax_rate:18 }] });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 lb-fade" data-testid="purchases-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Purchases</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Vendor Bills</h1>
          <p className="text-neutral-600 text-sm mt-1">ITC (Input Tax Credit) is auto-tracked from CGST/SGST/IGST Input ledgers.</p>
        </div>
        <button className="lb-btn" onClick={() => setShow((s) => !s)} data-testid="add-purchase-btn">
          <Plus size={14} /> New Bill
        </button>
      </div>

      {show && (
        <form onSubmit={submit} className="lb-card p-6 space-y-4" data-testid="purchase-form">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className="lb-label">Vendor</label>
              <select required className="lb-select" value={form.vendor_id}
                      onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} data-testid="pu-vendor">
                <option value="">Select vendor</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="lb-label">Bill No.</label>
              <input required className="lb-input font-mono" value={form.bill_no}
                     onChange={(e) => setForm({ ...form, bill_no: e.target.value })} data-testid="pu-bill-no" />
            </div>
            <div>
              <label className="lb-label">Currency</label>
              <select className="lb-select" value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                {["INR","USD","EUR","GBP","AED","SGD"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="lb-label">Bill Date</label>
              <input type="date" required className="lb-input" value={form.bill_date}
                     onChange={(e) => setForm({ ...form, bill_date: e.target.value })} />
            </div>
            <div>
              <label className="lb-label">Due Date</label>
              <input type="date" className="lb-input" value={form.due_date}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>

          <table className="lb-table">
            <thead>
              <tr><th>Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-right">GST %</th><th></th></tr>
            </thead>
            <tbody>
              {form.lines.map((l, i) => (
                <tr key={i}>
                  <td><input required className="lb-input" value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} data-testid={`pu-line-name-${i}`} /></td>
                  <td><input className="lb-input font-mono" value={l.hsn} onChange={(e) => setLine(i, { hsn: e.target.value })} /></td>
                  <td><input type="number" step="0.01" className="lb-input text-right font-mono" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                  <td><input type="number" step="0.01" className="lb-input text-right font-mono" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} /></td>
                  <td><input type="number" step="0.01" className="lb-input text-right font-mono" value={l.tax_rate} onChange={(e) => setLine(i, { tax_rate: e.target.value })} /></td>
                  <td><button type="button" onClick={() => removeLine(i)} className="text-neutral-500 hover:text-rose-600"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={addLine}><Plus size={12} /> Line</button>

          <div className="flex gap-2 pt-3 border-t border-neutral-200">
            <button className="lb-btn" data-testid="pu-submit">Save Bill</button>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShow(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="lb-card">
        <table className="lb-table">
          <thead><tr><th>Bill #</th><th>Date</th><th>Vendor</th><th>GSTIN</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono text-xs">{r.bill_no}</td>
                <td className="text-sm">{fmtDate(r.bill_date)}</td>
                <td className="text-sm">{r.vendor_snapshot?.name}</td>
                <td className="font-mono text-xs">{r.vendor_snapshot?.gstin || "—"}</td>
                <td className="amt">{fmtINR(r.total)}</td>
                <td><span className="lb-badge lb-badge-danger">{r.status}</span></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="text-center py-10 text-neutral-500">No purchase bills</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
